import { useState } from "react";
import { MODULES } from "../constants/modules";
import { COLORS, FONTS } from "../constants/theme";
import Logo from "../components/Logo";

// ─── ONBOARDING FLOW ──────────────────────────────────────────────────────────
// 3-step flow shown to new signups before entering the dashboard.
// Step 1: Role + team size
// Step 2: CRM selection
// Step 3: Module selection

const ROLES = [
  "CRO / Chief Revenue Officer",
  "VP of Sales",
  "VP of Marketing",
  "Head of RevOps",
  "CFO / Finance",
  "Customer Success Lead",
  "Other",
];
const SIZES = ["1–10", "11–50", "51–200", "201–500", "500+"];
const CRMS = ["Salesforce", "HubSpot", "Pipedrive", "Monday CRM", "Other / None"];

export default function OnboardingFlow({ user, onComplete }) {
  const [step, setStep] = useState(0);
  const [role, setRole] = useState("");
  const [teamSize, setTeamSize] = useState("");
  const [crm, setCrm] = useState("");
  const [selectedMods, setSelectedMods] = useState(["executive", "revenue", "sales"]);
  const [finishing, setFinishing] = useState(false);

  const toggleMod = (id) => {
    setSelectedMods((prev) =>
      prev.includes(id)
        ? prev.length > 1 ? prev.filter((m) => m !== id) : prev
        : [...prev, id]
    );
  };

  const handleFinish = () => {
    setFinishing(true);
    // TODO Phase 2: Save onboarding data to Supabase user profile
    setTimeout(() => onComplete({ role, teamSize, crm, selectedMods }), 1400);
  };

  const steps = [
    {
      title: `Welcome, ${user?.name?.split(" ")[0] || "there"} 👋`,
      sub: "Let's personalize your RevCentriQ in 3 quick steps.",
      canNext: !!role && !!teamSize,
      content: (
        <div>
          <SectionLabel>What's your role?</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {ROLES.map((r) => (
              <SelectButton key={r} selected={role === r} onClick={() => setRole(r)}>{r}</SelectButton>
            ))}
          </div>
          <SectionLabel style={{ marginTop: 28 }}>Team size</SectionLabel>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {SIZES.map((s) => (
              <SelectButton key={s} selected={teamSize === s} onClick={() => setTeamSize(s)} compact>{s}</SelectButton>
            ))}
          </div>
        </div>
      ),
    },
    {
      title: "Connect your stack",
      sub: "RevCentriQ pulls from your CRM to power live dashboards.",
      canNext: !!crm,
      content: (
        <div>
          <SectionLabel>Your CRM</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 24 }}>
            {CRMS.map((c) => (
              <SelectButton key={c} selected={crm === c} onClick={() => setCrm(c)} checkmark>{c}</SelectButton>
            ))}
          </div>
          {crm && crm !== "Other / None" && (
            <div style={{ background: "#05996912", border: "1px solid #05996930", borderRadius: 10, padding: "14px 18px", fontSize: 13, color: "#34d399" }}>
              ✓ We'll import your {crm} data in the background — your dashboard will be ready in minutes.
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Choose your modules",
      sub: "Pick the intelligence modules you want active from day one.",
      canNext: selectedMods.length > 0,
      content: (
        <div>
          <SectionLabel>
            Active modules{" "}
            <span style={{ color: COLORS.iqBlueMid }}>({selectedMods.length} selected)</span>
          </SectionLabel>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {MODULES.map((m) => {
              const sel = selectedMods.includes(m.id);
              return (
                <button
                  key={m.id}
                  onClick={() => toggleMod(m.id)}
                  style={{
                    background: sel ? m.accent + "12" : COLORS.bgElevated,
                    border: `1px solid ${sel ? m.accent + "55" : COLORS.border}`,
                    borderRadius: 10,
                    padding: "12px 14px",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all .15s",
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <div style={{ width: 26, height: 26, borderRadius: 7, background: sel ? m.accent + "22" : COLORS.border, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: sel ? m.accent : COLORS.textSecondary, flexShrink: 0 }}>
                    {m.icon}
                  </div>
                  <p style={{ fontFamily: FONTS.display, fontWeight: 700, fontSize: 12, color: sel ? m.accent : COLORS.textSecondary }}>
                    {m.label}
                  </p>
                  {sel && <span style={{ marginLeft: "auto", color: m.accent, fontSize: 12 }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      ),
    },
  ];

  const current = steps[step];

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bgBase, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "40px 20px", fontFamily: FONTS.body }}>
      {/* Background grid */}
      <div style={{ position: "fixed", inset: 0, backgroundImage: "linear-gradient(#1a2035 1px,transparent 1px),linear-gradient(90deg,#1a2035 1px,transparent 1px)", backgroundSize: "50px 50px", opacity: .18, pointerEvents: "none" }} />

      <div style={{ position: "relative", width: "100%", maxWidth: 560 }}>
        {/* Logo */}
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 40 }}>
          <Logo size="md" />
        </div>

        {/* Progress bar */}
        <div style={{ display: "flex", gap: 6, marginBottom: 40 }}>
          {steps.map((_, i) => (
            <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i <= step ? COLORS.iqBlue : COLORS.border, transition: "background .3s" }} />
          ))}
        </div>

        {/* Card */}
        <div style={{ background: COLORS.bgCard, border: `1px solid ${COLORS.border}`, borderRadius: 18, padding: "36px 36px 32px", boxShadow: "0 24px 80px #00000060" }}>
          <p style={{ fontFamily: FONTS.mono, fontSize: 11, color: COLORS.iqBlueMid, letterSpacing: ".08em", textTransform: "uppercase", marginBottom: 10 }}>
            Step {step + 1} of {steps.length}
          </p>
          <h2 style={{ fontFamily: FONTS.display, fontWeight: 800, fontSize: 26, letterSpacing: "-.03em", color: COLORS.textPrimary, marginBottom: 6 }}>
            {current.title}
          </h2>
          <p style={{ color: COLORS.textSecondary, fontSize: 14, marginBottom: 28 }}>{current.sub}</p>

          <div style={{ maxHeight: 420, overflowY: "auto", paddingRight: 4 }}>
            {current.content}
          </div>

          {/* Nav buttons */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 28, gap: 12 }}>
            {step > 0 ? (
              <button className="btn-ghost" onClick={() => setStep((s) => s - 1)}>← Back</button>
            ) : <div />}

            {step < steps.length - 1 ? (
              <button className="btn-primary" onClick={() => current.canNext && setStep((s) => s + 1)} style={{ opacity: current.canNext ? 1 : .4 }}>
                Continue →
              </button>
            ) : (
              <button className="btn-primary" onClick={handleFinish} style={{ opacity: finishing ? .7 : 1 }} disabled={finishing}>
                {finishing ? (
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 14, height: 14, border: "2px solid #fff4", borderTop: "2px solid #fff", borderRadius: "50%", display: "inline-block", animation: "spin-slow .7s linear infinite" }} />
                    Setting up your workspace…
                  </span>
                ) : "Launch RevCentriQ →"}
              </button>
            )}
          </div>
        </div>

        <p style={{ textAlign: "center", marginTop: 20, fontSize: 12, color: COLORS.textMuted, fontFamily: FONTS.mono }}>
          Logged in as {user?.email}
        </p>
      </div>
    </div>
  );
}

// ── Local helpers ──────────────────────────────────────────────────────────────

function SectionLabel({ children, style = {} }) {
  return (
    <p style={{ color: COLORS.textSecondary, fontSize: 13, fontFamily: FONTS.mono, letterSpacing: ".06em", textTransform: "uppercase", marginBottom: 14, ...style }}>
      {children}
    </p>
  );
}

function SelectButton({ children, selected, onClick, compact = false, checkmark = false }) {
  return (
    <button
      onClick={onClick}
      style={{
        background: selected ? "#1d4ed818" : COLORS.bgElevated,
        border: `1px solid ${selected ? COLORS.iqBlue + "66" : COLORS.border}`,
        borderRadius: compact ? 8 : 10,
        padding: compact ? "10px 18px" : "13px 18px",
        color: selected ? "#60a5fa" : "#a0aec0",
        cursor: "pointer",
        textAlign: "left",
        fontFamily: FONTS.body,
        fontSize: compact ? 13 : 14,
        transition: "all .15s",
        display: "flex",
        alignItems: "center",
        justifyContent: checkmark ? "space-between" : "flex-start",
      }}
    >
      {children}
      {checkmark && selected && <span style={{ color: COLORS.iqBlueMid }}>✓</span>}
    </button>
  );
}
